import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { createGlobalStyle } from 'styled-components'
import { Modal, Spin } from 'antd'
import { get } from 'lodash'

import { IconPDLSparkle } from '@edulastic/icons'
import { cream100, black } from '@edulastic/colors'
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageContent,
  GroupNameBadge,
  GroupSummaryText,
  GroupingContentWrapper,
  LegendContainer,
  LegendTitle,
  LegendList,
  LegendItem,
  LegendItemDescription,
} from './styled'
import GroupAccordion from './GroupAccordion'
import { fetchGroupAndAssignRecommendationsAction } from '../../../TestList/ducks'
import {
  getGroupsData,
  getNotGroupedStudents,
  getGroupAndAssignModalVisible,
  closeGroupAndAssignModalAction,
} from './ducks'
import {
  getCurriculumDropdownOptions,
  getAllInterestedCurriculumsSelector,
} from '../../selectors/user'
import { receiveStandardsProficiencyAction } from '../../../StandardsProficiency/ducks'
import {
  buildRecommendationGroupLists,
  isSoloSingleStudentGrouping,
} from './groupAndAssignUtils'
import StandardSetMasteryBandStep from './StandardSetMasteryBandStep'
import NotGroupedSection from './NotGroupedSection'
import { GROUP_AND_ASSIGN_FEEDBACK_SOURCE } from './constants'

const GroupAndAssignModalHeaderStyles = createGlobalStyle`
  .group-and-assign-modal-wrap .ant-modal {
    max-width: 100% !important;
    width: 100% !important;
    top: 0 !important;
    left: 0 !important;
    padding-bottom: 0 !important;
    margin: 0 !important;
    transform: none !important;
    height: calc(100vh-120px);
    max-height: 100vh;
  }
  .group-and-assign-modal-wrap .ant-modal-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .group-and-assign-modal-wrap .ant-modal-content .ant-modal-close {
    top: 15px;
    right: 20px;
    color: ${black};
  }
  .group-and-assign-modal-wrap .ant-modal-body {
    flex: 1;
    overflow: auto;
    display: flex;
    flex-direction: column;
  }
  .group-and-assign-modal-wrap .ant-modal-header {
    display: flex;
    align-items: center;
    background-color: ${cream100};
  }
  .group-and-assign-modal-wrap .ant-modal-title {
    flex: 1;
  }
`

const EMPTY_GROUPS = []
const EMPTY_PROFILE = { scale: [] }

const getMasteryBandsWithMasteredFallback = (profile) => {
  const scale = Array.isArray(profile?.scale) ? profile.scale : []
  if (!scale.length) return []

  const hasMasteredBand = scale.some(
    (band) => band?.domainMastery === true || band?.isMastered === true
  )
  if (hasMasteredBand) return scale

  let highestBandIndex = 0
  let highestThreshold = Number.NEGATIVE_INFINITY
  scale.forEach((band, index) => {
    const threshold = Number(band?.threshold)
    if (threshold > highestThreshold) {
      highestThreshold = threshold
      highestBandIndex = index
    }
  })

  return scale.map((band, index) =>
    index === highestBandIndex ? { ...band, domainMastery: true } : band
  )
}

/**
 * Mirrors ControlDropDown's searchItemOrDefault fallback.
 * When initialCurriculumId is missing or doesn't match any option key,
 * return the first option key to keep state aligned with the dropdown display.
 */
const resolveInitialCurriculumId = (
  initialCurriculumId,
  curriculumDropdownOptions
) => {
  if (!curriculumDropdownOptions?.length) return initialCurriculumId ?? null
  const idStr = initialCurriculumId != null ? String(initialCurriculumId) : ''
  const matches = idStr
    ? curriculumDropdownOptions.some((option) => option.key === idStr)
    : false
  return matches ? idStr : curriculumDropdownOptions[0].key
}

/**
 * Full-page modal for Group and Assign.
 * - ClassBoard (`showFilters`): pass `initialCurriculumId` / `initialProfileId` from assignment; filter step first, then Continue loads groups + recommendations.
 * - Gradebook (`!showFilters`): pass `initialProfileId` from report filters; groups pre-loaded; recommendations run with that profile’s scale + subgroup bands.
 * - Optional `standardsMasteryReportLinkContext`: `{ termId, classId }` so the “Standards mastery report” link deep-links SMR filters (class column / standard set).
 */
const GroupAndAssignPage = ({
  visible = false,
  groups,
  notGroupedStudents = [],
  initialCurriculumId = null,
  initialProfileId = null,
  standardsMasteryReportLinkContext = null,
  fetchGroupAndAssignRecommendations,
  onClose,
  onContinue = null,
  curriculumDropdownOptions,
  standardsProficiencyProfiles,
  standardsProficiencyProfilesLoading,
  standardsProficiencyProfilesError,
  receiveStandardsProficiency,
  showFilters = false,
  showLegend = false,
  showInitialLoader = false,
  defaultGrades = [],
  defaultSubjects = [],
  interestedCurriculums = [],
  reportType = null,
}) => {
  const [
    selectingStandardSetAndMasteryBand,
    setSelectingStandardSetAndMasteryBand,
  ] = useState(showFilters)
  const [selectedCurriculumId, setSelectedCurriculumId] = useState(
    initialCurriculumId
  )
  const [selectedProfileId, setSelectedProfileId] = useState(initialProfileId)
  const [isLoadingGroups, setIsLoadingGroups] = useState(false)
  const prevBlockingLoaderRef = useRef(true)

  const standardsProfilesBlocking =
    showFilters &&
    visible &&
    selectingStandardSetAndMasteryBand &&
    (standardsProficiencyProfilesLoading || !curriculumDropdownOptions.length)

  const showBlockingLoader = showInitialLoader || standardsProfilesBlocking

  // Handles both:
  // 1) first apply after LCB/bootstrap blocking loader clears, and
  // 2) late-arriving initialProfileId sync to test-level profile.
  useEffect(() => {
    if (!visible) {
      prevBlockingLoaderRef.current = true
      return
    }
    if (!selectingStandardSetAndMasteryBand) return
    const profiles = Array.isArray(standardsProficiencyProfiles)
      ? standardsProficiencyProfiles
      : []
    const initialProfileIdStr = initialProfileId ? String(initialProfileId) : ''
    const initialMatchesProfile =
      initialProfileIdStr &&
      profiles.some((p) => p?._id && String(p._id) === initialProfileIdStr)
    if (showBlockingLoader) {
      prevBlockingLoaderRef.current = true
      return
    }
    if (prevBlockingLoaderRef.current) {
      setSelectedCurriculumId(
        resolveInitialCurriculumId(
          initialCurriculumId,
          curriculumDropdownOptions
        )
      )
      setSelectedProfileId(
        initialMatchesProfile ? initialProfileId : profiles[0]?._id ?? null
      )
      prevBlockingLoaderRef.current = false
      return
    }
    if (
      initialMatchesProfile &&
      String(selectedProfileId || '') !== initialProfileIdStr
    ) {
      setSelectedProfileId(initialProfileId)
    }
  }, [
    visible,
    selectingStandardSetAndMasteryBand,
    showBlockingLoader,
    initialCurriculumId,
    initialProfileId,
    standardsProficiencyProfiles,
  ])

  // Remount stays mounted when the modal closes; re-apply props only when visibility changes,
  // not when initial* / showFilters change while the modal is open (user may be mid-flow).
  useEffect(() => {
    if (!visible) {
      setSelectingStandardSetAndMasteryBand(showFilters)
      return
    }
    setSelectingStandardSetAndMasteryBand(showFilters)
    setSelectedCurriculumId(
      showFilters
        ? resolveInitialCurriculumId(
            initialCurriculumId,
            curriculumDropdownOptions
          )
        : initialCurriculumId ?? null
    )
    const profiles = Array.isArray(standardsProficiencyProfiles)
      ? standardsProficiencyProfiles
      : []
    const initialMatchesProfile =
      initialProfileId &&
      profiles.some((p) => p?._id && String(p._id) === String(initialProfileId))
    setSelectedProfileId(
      initialMatchesProfile ? initialProfileId : profiles[0]?._id ?? null
    )
  }, [visible])

  useEffect(() => {
    if (
      !standardsProficiencyProfilesError &&
      !standardsProficiencyProfilesLoading &&
      !standardsProficiencyProfiles?.length
    ) {
      receiveStandardsProficiency()
    }
  }, [
    standardsProficiencyProfilesLoading,
    standardsProficiencyProfilesError,
    standardsProficiencyProfiles,
    receiveStandardsProficiency,
  ])

  const currentGroups = Array.isArray(groups) ? groups : EMPTY_GROUPS

  const selectedProficiencyProfile =
    standardsProficiencyProfiles.find(
      (profile) => profile?._id === selectedProfileId
    ) ||
    standardsProficiencyProfiles[0] ||
    EMPTY_PROFILE

  const profileIdForGroupRequest = useMemo(() => {
    const profiles = Array.isArray(standardsProficiencyProfiles)
      ? standardsProficiencyProfiles
      : []
    if (
      selectedProfileId &&
      profiles.some(
        (p) => p?._id && String(p._id) === String(selectedProfileId)
      )
    ) {
      return selectedProfileId
    }
    return selectedProficiencyProfile?._id ?? null
  }, [
    standardsProficiencyProfiles,
    selectedProfileId,
    selectedProficiencyProfile,
  ])

  const masteryBands = useMemo(
    () => getMasteryBandsWithMasteredFallback(selectedProficiencyProfile),
    [selectedProficiencyProfile]
  )

  const { testsGroups, decksGroups } = useMemo(
    () => buildRecommendationGroupLists(currentGroups),
    [currentGroups]
  )

  const soloSingleStudentView = useMemo(
    () => isSoloSingleStudentGrouping(currentGroups),
    [currentGroups]
  )

  const soloStudentName = useMemo(() => {
    if (!soloSingleStudentView) return ''
    for (const group of currentGroups) {
      for (const standardGroup of group?.standardGroups || []) {
        const student = (standardGroup?.students || [])[0]
        const name = student?.name || student?.studentName || ''
        if (name) return name
      }
    }
    return ''
  }, [soloSingleStudentView, currentGroups])

  // ClassBoard (`showFilters`): library filters use the selected standard set’s subject from Redux,
  // not assignment/test metadata. Gradebook keeps passing `defaultSubjects` when `showFilters` is false.
  const effectiveCurriculumId =
    selectedCurriculumId ?? initialCurriculumId ?? null

  const feedbackContext = useMemo(() => {
    const reportContext = {
      curriculumId: effectiveCurriculumId,
      profileId: profileIdForGroupRequest,
      profileName: selectedProficiencyProfile?.name ?? null,
      ...(standardsMasteryReportLinkContext || {}),
    }

    return {
      source: showFilters ? GROUP_AND_ASSIGN_FEEDBACK_SOURCE.LCB : reportType,
      reportContext,
    }
  }, [
    showFilters,
    reportType,
    effectiveCurriculumId,
    profileIdForGroupRequest,
    selectedProficiencyProfile?.name,
    standardsMasteryReportLinkContext,
  ])

  const effectiveDefaultSubjects = useMemo(() => {
    if (!showFilters) return defaultSubjects
    const idStr =
      selectedCurriculumId != null ? String(selectedCurriculumId) : ''
    if (!idStr) return []
    const match = interestedCurriculums.find(
      (c) => c && String(c._id) === idStr
    )
    return match?.subject ? [match.subject] : []
  }, [
    showFilters,
    defaultSubjects,
    selectedCurriculumId,
    interestedCurriculums,
  ])

  useEffect(() => {
    if (!visible) return
    if (showBlockingLoader) return
    // LCB: Redux may still hold the previous session's groups while the user is on
    // the standard set / mastery band step — do not fetch with that stale data.
    if (showFilters && selectingStandardSetAndMasteryBand) return
    if (testsGroups.length === 0 && decksGroups.length === 0) return
    fetchGroupAndAssignRecommendations({
      testsGroups,
      decksGroups,
      masteryBands,
    })
  }, [
    visible,
    showBlockingLoader,
    showFilters,
    selectingStandardSetAndMasteryBand,
    testsGroups,
    decksGroups,
    masteryBands,
    fetchGroupAndAssignRecommendations,
  ])

  if (!visible) return null

  return (
    <>
      <GroupAndAssignModalHeaderStyles />
      <Modal
        wrapClassName="group-and-assign-modal-wrap"
        title={
          <PageHeader>
            <PageTitle>
              <IconPDLSparkle width={22} height={22} /> Identify learning gaps
            </PageTitle>
          </PageHeader>
        }
        visible={visible}
        onCancel={onClose}
        footer={null}
        width="100%"
        style={{ top: 0, paddingBottom: 0, maxWidth: '100%' }}
        bodyStyle={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          backgroundColor: cream100,
        }}
        destroyOnClose
      >
        <PageContainer>
          <PageContent>
            {showBlockingLoader ? (
              <div
                style={{
                  minHeight: '50vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                }}
              >
                <Spin size="large" />
              </div>
            ) : selectingStandardSetAndMasteryBand ? (
              <StandardSetMasteryBandStep
                curriculumDropdownOptions={curriculumDropdownOptions}
                selectedCurriculumId={selectedCurriculumId}
                setSelectedCurriculumId={setSelectedCurriculumId}
                standardsProficiencyProfiles={standardsProficiencyProfiles}
                selectedProficiencyProfile={selectedProficiencyProfile}
                setSelectedProfileId={setSelectedProfileId}
                isLoadingGroups={isLoadingGroups}
                setIsLoadingGroups={setIsLoadingGroups}
                onContinueWithProfileAndCurriculum={
                  onContinue
                    ? () =>
                        onContinue(
                          profileIdForGroupRequest,
                          selectedCurriculumId
                        )
                    : null
                }
                onStepComplete={() =>
                  setSelectingStandardSetAndMasteryBand(false)
                }
              />
            ) : (
              <>
                {currentGroups.length > 0 ? (
                  <GroupingContentWrapper>
                    <GroupSummaryText>
                      {soloSingleStudentView
                        ? `Recommended learning materials${
                            soloStudentName ? ` for ${soloStudentName}` : ''
                          }`
                        : `Recommended learning materials for ${
                            currentGroups.length
                          } student group${
                            currentGroups.length === 1 ? '' : 's'
                          }.`}
                    </GroupSummaryText>
                    {currentGroups.map((group) =>
                      (group.standardGroups || []).length > 0 ? (
                        <GroupAccordion
                          key={group.name}
                          group={group}
                          soloSingleStudentView={soloSingleStudentView}
                          includeTestContext={showLegend}
                          defaultGrades={defaultGrades}
                          defaultSubjects={effectiveDefaultSubjects}
                          curriculumId={effectiveCurriculumId}
                          feedbackContext={feedbackContext}
                        />
                      ) : null
                    )}
                    {notGroupedStudents.length > 0 && (
                      <NotGroupedSection
                        notGroupedStudents={notGroupedStudents}
                        selectedCurriculumId={selectedCurriculumId}
                        selectedProfileId={selectedProfileId}
                        standardsMasteryReportLinkContext={
                          standardsMasteryReportLinkContext
                        }
                      />
                    )}
                    {showLegend && (
                      <LegendContainer>
                        <LegendTitle>What each group means</LegendTitle>
                        <LegendList>
                          {currentGroups.map((group) => (
                            <LegendItem key={group.name}>
                              <GroupNameBadge
                                $bgColor={group.bgColor}
                                $textColor={group.textColor}
                              >
                                {group.name ?? '—'}
                              </GroupNameBadge>
                              <LegendItemDescription>
                                {group.description || '—'}
                              </LegendItemDescription>
                            </LegendItem>
                          ))}
                        </LegendList>
                      </LegendContainer>
                    )}
                  </GroupingContentWrapper>
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#999',
                    }}
                  >
                    No groups available or All students unenrolled for class.
                  </div>
                )}
              </>
            )}
          </PageContent>
        </PageContainer>
      </Modal>
    </>
  )
}

const mapStateToProps = (state) => ({
  visible: getGroupAndAssignModalVisible(state),
  groups: getGroupsData(state),
  notGroupedStudents: getNotGroupedStudents(state),
  curriculumDropdownOptions: getCurriculumDropdownOptions(state),
  interestedCurriculums: getAllInterestedCurriculumsSelector(state),
  standardsProficiencyProfiles: get(
    state,
    ['standardsProficiencyReducer', 'data'],
    []
  ),
  standardsProficiencyProfilesLoading: get(
    state,
    ['standardsProficiencyReducer', 'loading'],
    false
  ),
  standardsProficiencyProfilesError: get(
    state,
    ['standardsProficiencyReducer', 'error'],
    false
  ),
})

export default connect(mapStateToProps, {
  fetchGroupAndAssignRecommendations: fetchGroupAndAssignRecommendationsAction,
  onClose: closeGroupAndAssignModalAction,
  receiveStandardsProficiency: receiveStandardsProficiencyAction,
})(GroupAndAssignPage)
